import { Client, Osdk } from "@osdk/client";
import {
  QueryClient,
  useMutation,
  useQuery,
  UseQueryResult,
} from "@tanstack/react-query";
import {
  createEventDeepDiveRequestForUser,
  createTopicDeepDiveRequestForUser,
  createCustomReportRequest,
  createDyad,
  createReportCollection,
  updateDyadConfig,
  generateAllDyadAssessments,
  Country,
  Request,
} from "@unruly-osdk/sdk";

const POLLING_INTERVAL_IN_MS = 5000;
const MAX_OLD_REQUEST_AGE_IN_MS = 5 * 60 * 1000;
const MAX_CUSTOM_REPORT_REQUEST_AGE_IN_MS = 15 * 60 * 1000;

const keys = {
  all: ["requests"] as const,
  byEventAndUser: (eventId: string, userId: string) =>
    [...keys.all, "eventId", eventId, "userId", userId] as const,
  byTopicAndUser: (topic: string, userId: string) =>
    [...keys.all, "topic", topic, "userId", userId] as const,
  byUserIdForTopicDeepDiveRequest: (userId: string) =>
    [
      ...keys.all,
      "userId",
      userId,
      "requestType",
      "Topic Deep Dive Request",
    ] as const,
  byUserIdForEventDeepDiveRequest: (userId: string) =>
    [
      ...keys.all,
      "userId",
      userId,
      "requestType",
      "Event Deep Dive Request",
    ] as const,
  byUserIdForCustomReportRequest: (userId: string) =>
    [
      ...keys.all,
      "userId",
      userId,
      "requestType",
      "Custom Report Generation",
    ] as const,
  byUserIdForDyadReportRequest: (userId: string) =>
    [
      ...keys.all,
      "userId",
      userId,
      "requestType",
      "Dyad Report Generation",
    ] as const,
};

function RequestService(queryClient: QueryClient, apiClient: Client) {
  const service = {
    useGetCountries: () => {
      return useQuery({
        queryKey: ["countries"],
        queryFn: async () => {
          const res = await apiClient(Country)
            .where({})
            .fetchPage({
              $pageSize: 300,
              $select: ["countryId", "name"],
            });
          return res.data;
        },
        staleTime: Infinity,
      });
    },

    useGetEventDeepDiveRequest: (
      eventId: string,
      userId: string,
    ): UseQueryResult<Osdk.Instance<Request> | null> => {
      return useQuery({
        queryKey: keys.byEventAndUser(eventId, userId),
        queryFn: async () => service.getEventDeepDiveRequest(eventId, userId),
        enabled: !!eventId && !!userId,
        refetchInterval: (query) => {
          if (!query.state.data) return false;
          const request = query.state.data as Osdk.Instance<Request>;
          // Stop polling if request is in final state or if it was created more than x minutes ago
          return (request.status === "New" || request.status === "Pending") &&
            (request.createdAt
              ? new Date(request.createdAt) >
                new Date(Date.now() - MAX_OLD_REQUEST_AGE_IN_MS)
              : true)
            ? POLLING_INTERVAL_IN_MS
            : false;
        },
        refetchIntervalInBackground: true,
      });
    },

    getEventDeepDiveRequest: async (
      eventId: string,
      userId: string,
    ): Promise<Osdk.Instance<Request> | null> => {
      const response = await apiClient(Request)
        .where({
          eventId: eventId,
          createdBy: userId,
          requestType: "Event Deep Dive Request",
        })
        .fetchPage({
          $orderBy: { createdAt: "desc" },
        });
      return response.data?.[0] || null;
    },

    createEventDeepDiveRequest: async (
      eventId: string,
      userId: string,
    ): Promise<void> => {
      await apiClient(createEventDeepDiveRequestForUser).applyAction({
        event_id: eventId,
        user_id: userId,
      });
    },

    useCreateEventDeepDiveRequest: () => {
      return useMutation({
        mutationFn: async ({
          eventId,
          userId,
        }: {
          eventId: string;
          userId: string;
        }) => {
          await service.createEventDeepDiveRequest(eventId, userId);
        },
        onSuccess: async (_data, { eventId, userId }) => {
          await queryClient.invalidateQueries({
            queryKey: keys.byEventAndUser(eventId, userId),
          });
          await queryClient.invalidateQueries({
            queryKey: keys.byUserIdForEventDeepDiveRequest(userId),
          });
        },
      });
    },

    createTopicDeepDiveRequest: async (
      topic: string,
      userId: string,
    ): Promise<void> => {
      await apiClient(createTopicDeepDiveRequestForUser).applyAction({
        payload: topic,
        user_id: userId,
      });
    },

    useGetTopicDeepDiveRequest: (
      topic: string,
      userId: string,
    ): UseQueryResult<Osdk.Instance<Request> | null> => {
      return useQuery({
        queryKey: keys.byTopicAndUser(topic, userId),
        queryFn: async () => service.getTopicDeepDiveRequest(topic, userId),
        enabled: !!topic && !!userId,
        refetchInterval: (query) => {
          if (!query.state.data) return false;
          const request = query.state.data as Osdk.Instance<Request>;
          // Stop polling if request is in final state or if it was created more than x minutes ago
          return (request.status === "New" || request.status === "Pending") &&
            (request.createdAt
              ? new Date(request.createdAt) >
                new Date(Date.now() - MAX_OLD_REQUEST_AGE_IN_MS)
              : true)
            ? POLLING_INTERVAL_IN_MS
            : false;
        },
        refetchIntervalInBackground: true,
      });
    },

    getTopicDeepDiveRequest: async (
      topic: string,
      userId: string,
    ): Promise<Osdk.Instance<Request> | null> => {
      const response = await apiClient(Request)
        .where({
          payload: topic,
          createdBy: userId,
          requestType: "Topic Deep Dive Request",
        })
        .fetchPage({
          $orderBy: { createdAt: "desc" },
        });
      return response.data?.[0] || null;
    },

    useCreateTopicDeepDiveRequest: () => {
      return useMutation({
        mutationFn: async ({
          topic,
          userId,
        }: {
          topic: string;
          userId: string;
        }) => {
          await service.createTopicDeepDiveRequest(topic, userId);
        },
        onSuccess: async (_data, { topic, userId }) => {
          await queryClient.invalidateQueries({
            queryKey: keys.byTopicAndUser(topic, userId),
          });
          await queryClient.invalidateQueries({
            queryKey: keys.byUserIdForTopicDeepDiveRequest(userId),
          });
        },
      });
    },

    subscribeToTopicDeepDiveRequest: async (
      userId: string,
    ): Promise<{
      unsubscribe: () => void;
    }> => {
      // Initialize cache by fetching existing requests
      await queryClient.fetchQuery({
        queryKey: keys.byUserIdForTopicDeepDiveRequest(userId),
        queryFn: async () => {
          const response = await apiClient(Request)
            .where({
              createdBy: userId,
              requestType: "Topic Deep Dive Request",
              createdAt: {
                $gt: new Date(
                  Date.now() - MAX_OLD_REQUEST_AGE_IN_MS,
                ).toISOString(),
              },
            })
            .fetchPage({
              $select: ["id", "createdAt", "status", "payload", "resolvedAt"],
            });
          return response.data || [];
        },
      });

      // Track subscription readiness
      let subscriptionReady = false;
      let unsubscribeFn: (() => void) | null = null;

      // Now set up subscription for real-time updates
      const subscriptionService = apiClient(Request)
        .where({
          createdBy: userId,
          requestType: "Topic Deep Dive Request",
          createdAt: {
            $gt: new Date(Date.now() - MAX_OLD_REQUEST_AGE_IN_MS).toISOString(),
          },
        })
        .subscribe(
          {
            onChange: (update) => {
              queueMicrotask(() => {
                if (update.state === "ADDED_OR_UPDATED") {
                  queryClient.setQueryData(
                    keys.byUserIdForTopicDeepDiveRequest(userId),
                    (old: Osdk.Instance<Request>[] | undefined) => {
                      const existing = old || [];
                      const exists = existing.some(
                        (req) => req.id === update.object.id,
                      );
                      if (exists) {
                        return existing.map((req) =>
                          req.id === update.object.id ? update.object : req,
                        ) as Osdk.Instance<Request>[];
                      }
                      return [
                        ...existing,
                        update.object,
                      ] as Osdk.Instance<Request>[];
                    },
                  );
                } else if (update.state === "REMOVED") {
                  queryClient.setQueryData(
                    keys.byUserIdForTopicDeepDiveRequest(userId),
                    (old: Osdk.Instance<Request>[] | undefined) => {
                      return old?.filter(
                        (request) => request.id !== update.object.id,
                      ) as Osdk.Instance<Request>[];
                    },
                  );
                }
              });
            },
            onError: (error) => {
              console.error(
                "Error subscribing to topic deep dive request:",
                error,
              );
            },
            onOutOfDate: () => {
              queryClient.invalidateQueries({
                queryKey: keys.byUserIdForTopicDeepDiveRequest(userId),
              });
            },
            onSuccessfulSubscription: () => {
              // Subscription established successfully, now waiting for changes
              subscriptionReady = true;
            },
          },
          {
            properties: ["id", "createdAt", "status", "payload", "resolvedAt"],
          },
        );

      unsubscribeFn = subscriptionService.unsubscribe;

      // Return safe unsubscribe wrapper
      return {
        unsubscribe: () => {
          if (!unsubscribeFn) {
            return;
          }
          try {
            // Only unsubscribe if subscription is ready
            // If not ready, the unsubscribe will be handled by error handling in useSubscription
            if (subscriptionReady) {
              unsubscribeFn();
            }
          } catch (error) {
            // Ignore WebSocket state errors (e.g., CONNECTING state)
            // These are expected when unsubscribe is called before subscription is ready
            if (
              error instanceof Error &&
              error.name === "InvalidStateError" &&
              error.message.includes("CONNECTING state")
            ) {
              // Silently ignore - WebSocket wasn't ready yet
              return;
            }
            // Log other unexpected errors
            console.warn("Error during topic deep dive unsubscribe:", error);
          }
        },
      };
    },

    useGetTrackedTopicDeepDiveRequests: (userId: string) => {
      return useQuery({
        queryKey: keys.byUserIdForTopicDeepDiveRequest(userId),
        queryFn: async () => {
          const response = await apiClient(Request)
            .where({
              createdBy: userId,
              requestType: "Topic Deep Dive Request",
              createdAt: {
                $gt: new Date(
                  Date.now() - MAX_OLD_REQUEST_AGE_IN_MS,
                ).toISOString(),
              },
            })
            .fetchPage({
              $select: ["id", "createdAt", "status", "payload", "resolvedAt"],
            });
          return response.data || [];
        },
        enabled: !!userId,
        refetchOnMount: true,
      });
    },

    subscribeToEventDeepDiveRequest: async (
      userId: string,
      callback: (update: Osdk.Instance<Request>) => void,
    ): Promise<{
      unsubscribe: () => void;
    }> => {
      // Initialize cache by fetching existing requests
      await queryClient.fetchQuery({
        queryKey: keys.byUserIdForEventDeepDiveRequest(userId),
        queryFn: async () => {
          const response = await apiClient(Request)
            .where({
              createdBy: userId,
              requestType: "Event Deep Dive Request",
              createdAt: {
                $gt: new Date(
                  Date.now() - MAX_OLD_REQUEST_AGE_IN_MS,
                ).toISOString(),
              },
            })
            .fetchPage({
              $select: ["id", "createdAt", "status", "eventId", "resolvedAt"],
            });
          return response.data || [];
        },
      });

      // Track subscription readiness
      let subscriptionReady = false;
      let unsubscribeFn: (() => void) | null = null;

      // Now set up subscription for real-time updates
      const subscriptionService = apiClient(Request)
        .where({
          createdBy: userId,
          requestType: "Event Deep Dive Request",
          createdAt: {
            $gt: new Date(Date.now() - MAX_OLD_REQUEST_AGE_IN_MS).toISOString(),
          },
        })
        .subscribe(
          {
            onChange: (update) => {
              callback(update.object as Osdk.Instance<Request>);
              queueMicrotask(() => {
                if (update.state === "ADDED_OR_UPDATED") {
                  queryClient.setQueryData(
                    keys.byUserIdForEventDeepDiveRequest(userId),
                    (old: Osdk.Instance<Request>[] | undefined) => {
                      const existing = old || [];
                      const exists = existing.some(
                        (req) => req.id === update.object.id,
                      );
                      if (exists) {
                        return existing.map((req) =>
                          req.id === update.object.id ? update.object : req,
                        ) as Osdk.Instance<Request>[];
                      }
                      return [
                        ...existing,
                        update.object,
                      ] as Osdk.Instance<Request>[];
                    },
                  );
                } else if (update.state === "REMOVED") {
                  queryClient.setQueryData(
                    keys.byUserIdForEventDeepDiveRequest(userId),
                    (old: Osdk.Instance<Request>[] | undefined) => {
                      return old?.filter(
                        (request) => request.id !== update.object.id,
                      ) as Osdk.Instance<Request>[];
                    },
                  );
                }
              });
            },
            onError: (error) => {
              console.error(
                "Error subscribing to event deep dive request:",
                error,
              );
            },
            onOutOfDate: () => {
              queryClient.invalidateQueries({
                queryKey: keys.byUserIdForEventDeepDiveRequest(userId),
              });
            },
            onSuccessfulSubscription: () => {
              // Subscription established successfully, now waiting for changes
              subscriptionReady = true;
            },
          },
          {
            properties: ["id", "createdAt", "status", "eventId", "resolvedAt"],
          },
        );

      unsubscribeFn = subscriptionService.unsubscribe;

      // Return safe unsubscribe wrapper
      return {
        unsubscribe: () => {
          if (!unsubscribeFn) {
            return;
          }
          try {
            // Only unsubscribe if subscription is ready
            // If not ready, the unsubscribe will be handled by error handling in useSubscription
            if (subscriptionReady) {
              unsubscribeFn();
            }
          } catch (error) {
            // Ignore WebSocket state errors (e.g., CONNECTING state)
            // These are expected when unsubscribe is called before subscription is ready
            if (
              error instanceof Error &&
              error.name === "InvalidStateError" &&
              error.message.includes("CONNECTING state")
            ) {
              // Silently ignore - WebSocket wasn't ready yet
              return;
            }
            // Log other unexpected errors
            console.warn("Error during event deep dive unsubscribe:", error);
          }
        },
      };
    },

    useGetTrackedEventDeepDiveRequests: (userId: string) => {
      return useQuery({
        queryKey: keys.byUserIdForEventDeepDiveRequest(userId),
        queryFn: async () => {
          const response = await apiClient(Request)
            .where({
              createdBy: userId,
              requestType: "Event Deep Dive Request",
              createdAt: {
                $gt: new Date(
                  Date.now() - MAX_OLD_REQUEST_AGE_IN_MS,
                ).toISOString(),
              },
            })
            .fetchPage({
              $select: ["id", "createdAt", "status", "eventId", "resolvedAt"],
            });
          return response.data || [];
        },
        enabled: !!userId,
        refetchOnMount: true,
      });
    },

    createCustomReportRequest: async (
      reportType: string,
      sessionName: string,
      userId: string,
      comment?: string,
    ): Promise<void> => {
      await apiClient(createCustomReportRequest).applyAction({
        "report-type": reportType,
        "session-name": sessionName,
        owner: userId,
        comment: comment ?? "",
      });
    },

    useCreateCustomReportRequest: () => {
      return useMutation({
        mutationFn: async ({
          reportType,
          sessionName,
          userId,
          comment,
        }: {
          reportType: string;
          sessionName: string;
          userId: string;
          comment?: string;
        }) => {
          await service.createCustomReportRequest(
            reportType,
            sessionName,
            userId,
            comment,
          );
        },
        onSuccess: async (_data, { userId }) => {
          await queryClient.invalidateQueries({
            queryKey: keys.byUserIdForCustomReportRequest(userId),
          });
        },
      });
    },

    generateAllDyadAssessments: async (
      userId: string,
      lookbackDays: number,
    ): Promise<void> => {
      await apiClient(generateAllDyadAssessments).applyAction({
        "created-by": userId,
        initiator: "User",
        "lookback-days": lookbackDays,
      });
    },

    useGenerateAllDyadAssessments: () => {
      return useMutation({
        mutationFn: async ({
          userId,
          lookbackDays,
        }: {
          userId: string;
          lookbackDays: number;
        }) => {
          await service.generateAllDyadAssessments(userId, lookbackDays);
        },
        onSuccess: async (_data, { userId }) => {
          await queryClient.invalidateQueries({
            queryKey: keys.byUserIdForCustomReportRequest(userId),
          });
          await queryClient.invalidateQueries({
            queryKey: keys.byUserIdForDyadReportRequest(userId),
          });
        },
      });
    },

    useGetTrackedCustomReportRequests: (userId: string) => {
      return useQuery({
        queryKey: keys.byUserIdForCustomReportRequest(userId),
        queryFn: async () => {
          const response = await apiClient(Request)
            .where({
              createdBy: userId,
              requestType: "Custom Report Generation",
              createdAt: {
                $gt: new Date(
                  Date.now() - MAX_CUSTOM_REPORT_REQUEST_AGE_IN_MS,
                ).toISOString(),
              },
            })
            .fetchPage({
              $select: [
                "id",
                "createdAt",
                "status",
                "payload",
                "resolvedAt",
                "customReportId",
              ],
            });
          return response.data || [];
        },
        enabled: !!userId,
        refetchOnMount: true,
        refetchInterval: (query) => {
          const requests = query.state.data as
            Osdk.Instance<Request>[] | undefined;
          const hasPending = requests?.some(
            (r) =>
              (r.status === "New" || r.status === "Pending") &&
              (r.createdAt
                ? new Date(r.createdAt) >
                  new Date(Date.now() - MAX_CUSTOM_REPORT_REQUEST_AGE_IN_MS)
                : true),
          );
          return hasPending ? 10_000 : false;
        },
        refetchIntervalInBackground: true,
      });
    },

    createDyadAction: async (params: {
      countryA: string;
      countryB: string;
      company: string;
      companyProfileContext?: string;
      userId: string;
    }): Promise<void> => {
      // SDK 0.92.0 has apiName 'createDyad' but Foundry action is 'create-dyad' — fix after next SDK regen
      const action = {
        ...createDyad,
        apiName: "create-dyad",
      } as unknown as typeof createDyad;
      await apiClient(action).applyAction({
        "country-a": params.countryA,
        "country-b": params.countryB,
        company: params.company,
        "company-profile-context": params.companyProfileContext || undefined,
        "created-by-user-id": params.userId,
      });
    },

    useCreateDyad: () => {
      return useMutation({
        mutationFn: async (params: {
          countryA: string;
          countryB: string;
          company: string;
          companyProfileContext?: string;
          userId: string;
        }) => {
          await service.createDyadAction(params);
        },
      });
    },

    updateDyadConfigAction: async (params: {
      dyadId: string;
      companyProfileContext?: string;
    }): Promise<void> => {
      await apiClient(updateDyadConfig).applyAction({
        dyad: params.dyadId,
        "company-profile-context": params.companyProfileContext || undefined,
      });
    },

    useUpdateDyadConfig: () => {
      return useMutation({
        mutationFn: async (params: {
          dyadId: string;
          companyProfileContext?: string;
        }) => {
          await service.updateDyadConfigAction(params);
        },
      });
    },

    createReportCollectionRequest: async (params: {
      bundleTitle: string;
      bundleSubtitle: string;
      periodStart: string;
      periodEnd: string;
      reportTypes: string[];
      userId?: string;
    }): Promise<void> => {
      await apiClient(createReportCollection).applyAction({
        "bundle-title": params.bundleTitle,
        "bundle-subtitle": params.bundleSubtitle,
        "period-start": params.periodStart,
        "period-end": params.periodEnd,
        "report-types": params.reportTypes,
        "generated-by": params.userId ?? null,
      });
    },

    useCreateReportCollection: () => {
      return useMutation({
        mutationFn: async (params: {
          bundleTitle: string;
          bundleSubtitle: string;
          periodStart: string;
          periodEnd: string;
          reportTypes: string[];
          userId?: string;
        }) => {
          await service.createReportCollectionRequest(params);
        },
        onSuccess: async (_data, { userId }) => {
          if (userId) {
            await queryClient.invalidateQueries({
              queryKey: keys.byUserIdForCustomReportRequest(userId),
            });
          }
          await queryClient.invalidateQueries({
            queryKey: ["customReports"],
          });
        },
      });
    },

    useGetTrackedDyadReportRequests: (userId: string) => {
      return useQuery({
        queryKey: keys.byUserIdForDyadReportRequest(userId),
        queryFn: async () => {
          const response = await apiClient(Request)
            .where({
              createdBy: userId,
              requestType: "Dyad Report Generation",
              createdAt: {
                $gt: new Date(
                  Date.now() - MAX_CUSTOM_REPORT_REQUEST_AGE_IN_MS,
                ).toISOString(),
              },
            })
            .fetchPage({
              $select: [
                "id",
                "createdAt",
                "status",
                "payload",
                "resolvedAt",
                "customReportId",
              ],
            });
          return response.data || [];
        },
        enabled: !!userId,
        refetchOnMount: true,
        refetchInterval: (query) => {
          const requests = query.state.data as
            Osdk.Instance<Request>[] | undefined;
          const hasPending = requests?.some(
            (r) =>
              (r.status === "New" || r.status === "Pending") &&
              (r.createdAt
                ? new Date(r.createdAt) >
                  new Date(Date.now() - MAX_CUSTOM_REPORT_REQUEST_AGE_IN_MS)
                : true),
          );
          return hasPending ? 10_000 : false;
        },
        refetchIntervalInBackground: true,
      });
    },
  };
  return service;
}

export default RequestService;
